import React, { useState, useEffect } from 'react';
import {
  Shield,
  Search,
  RefreshCw,
  Calendar,
  User,
  Activity
} from 'lucide-react';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const formatActionLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const getActionBadge = (actionType) => {
  const badges = {
    plan_create: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Created' },
    plan_update: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Updated' },
    plan_change: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Changed' },
    entitlement_update: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Modified' },
    system_config_update: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Config' },
  };
  return badges[actionType] || { bg: 'bg-slate-100', text: 'text-slate-600', label: formatActionLabel(actionType) };
};

const renderChanges = (log) => {
  try {
    const after = log.after_json
      ? (typeof log.after_json === 'string' ? JSON.parse(log.after_json) : log.after_json)
      : null;
    const before = log.before_json
      ? (typeof log.before_json === 'string' ? JSON.parse(log.before_json) : log.before_json)
      : null;

    if (before && after) {
      const changed = Object.keys(after).filter(
        k => JSON.stringify(after[k]) !== JSON.stringify(before[k])
      );
      if (changed.length === 0) return <span className="text-slate-400 italic text-[11px]">no change</span>;
      return (
        <span className="text-[11px]">
          {changed.slice(0, 3).map((k, i) => (
            <span key={k}>
              {i > 0 && <span className="text-slate-300">, </span>}
              <span className="text-slate-500">{k}:</span>{' '}
              <span className="text-rose-500 line-through">{String(before[k])}</span>
              <span className="text-slate-400"> → </span>
              <span className="text-emerald-600">{String(after[k])}</span>
            </span>
          ))}
          {changed.length > 3 && <span className="text-slate-400"> +{changed.length - 3} more</span>}
        </span>
      );
    }

    const raw = JSON.stringify(after || before || {});
    return (
      <span className="truncate block max-w-[200px] font-mono text-[11px]" title={raw}>
        {raw.substring(0, 60)}{raw.length > 60 ? '...' : ''}
      </span>
    );
  } catch {
    const raw = String(log.after_json || log.before_json || '-');
    return <span className="truncate block max-w-[200px] font-mono text-[11px]">{raw.substring(0, 60)}</span>;
  }
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchAuditLogs();
  }, [filterType]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);

      const params = { limit: 100 };
      if (filterType !== 'all') {
        params.entity_type = filterType;
      }

      const { data } = await api.get('/admin/audit-logs', { params });
      setLogs(data.logs || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load audit logs'));
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      log.action_type?.toLowerCase().includes(q) ||
      log.entity_type?.toLowerCase().includes(q) ||
      log.entity_name?.toLowerCase().includes(q) ||
      log.admin_email?.toLowerCase().includes(q)
    );
  });

  const recentCount = logs.filter(l => {
    const d = new Date(l.created_at);
    return (Date.now() - d) < 24 * 60 * 60 * 1000;
  }).length;

  if (loading) {
    return (
      <div className="h-full flex flex-col" data-testid="audit-logs-page">
        <ListPageHeader title="Audit Logs" stats={[]} />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-[3px] border-slate-200 border-t-rose-500 rounded-full animate-spin"></div>
            <p className="text-[13px] text-slate-500 font-normal">Loading audit logs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="audit-logs-page">
      <ListPageHeader
        title="Audit Logs"
        stats={[
          { icon: Shield, label: 'Total Logs', value: totalCount, color: 'slate' },
          { icon: Activity, label: 'Recent', value: recentCount, color: 'blue' },
        ]}
        ctaLabel="Refresh"
        ctaIcon={RefreshCw}
        onCtaClick={fetchAuditLogs}
        extraControls={
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-[13px] w-56 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400"
            >
              <option value="all">All Types</option>
              <option value="plan">Plans</option>
              <option value="business">Business</option>
              <option value="config">Config</option>
            </select>
          </div>
        }
      />

      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {filteredLogs.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No audit logs found</p>
              <p className="text-[13px] text-slate-400 mt-1">Admin actions will be logged here</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{width: '18%'}} />
                    <col style={{width: '15%'}} />
                    <col style={{width: '12%'}} />
                    <col style={{width: '12%'}} />
                    <col style={{width: '18%'}} />
                    <col style={{width: '25%'}} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Timestamp
                        </div>
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          Admin
                        </div>
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Action
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Entity Type
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Entity
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Changes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredLogs.map((log, index) => {
                      const actionBadge = getActionBadge(log.action_type);
                      return (
                        <tr
                          key={log.id}
                          className={`tl-tr hover:bg-rose-50/30 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        >
                          <td className="px-4 py-3 text-[12px] text-slate-600">
                            {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-[12px] text-slate-600">
                            {log.admin_email || 'System'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-[10px] font-medium ${actionBadge.bg} ${actionBadge.text}`}>
                              {actionBadge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[12px] text-slate-600">
                            {log.entity_type || '-'}
                          </td>
                          <td className="px-4 py-3 text-[12px] text-slate-700 font-medium">
                            {log.entity_name || log.entity_id || '-'}
                          </td>
                          <td className="px-4 py-3">
                            {renderChanges(log)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="tl-table-footer">
                Showing {filteredLogs.length} of {totalCount} audit logs
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
